import { useState } from 'react';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import type { ChecklistItem } from './progress';

const useStyles = makeStyles(theme => ({
  pill: {
    borderRadius: 999,
    padding: theme.spacing(1, 2),
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: theme.shadows[6],
  },
  count: {
    marginLeft: theme.spacing(1),
    padding: '0 7px',
    borderRadius: 999,
    fontSize: 12,
    lineHeight: '20px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
  },
  panel: {
    width: 360,
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  header: { padding: theme.spacing(2, 2, 1.5, 2.5) },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progress: { marginTop: theme.spacing(1.5), height: 6, borderRadius: 3 },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    width: '100%',
    textAlign: 'left',
    padding: theme.spacing(1.25, 2.5),
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  check: { marginTop: 1, color: theme.palette.primary.main },
  todo: { marginTop: 1, color: theme.palette.text.disabled },
  itemText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  itemDone: { color: theme.palette.text.secondary },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

export function Checklist(props: {
  items: Array<ChecklistItem & { done: boolean }>;
  onOpenItem: (item: ChecklistItem) => void;
  onReplayTour: () => void;
  onHide: () => void;
}) {
  const { items, onOpenItem, onReplayTour, onHide } = props;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const doneCount = items.filter(i => i.done).length;

  if (!open) {
    return (
      <Button
        variant="contained"
        color="primary"
        className={classes.pill}
        onClick={() => setOpen(true)}
      >
        Premiers pas
        <span className={classes.count}>
          {doneCount}/{items.length}
        </span>
      </Button>
    );
  }

  return (
    <Paper elevation={8} className={classes.panel}>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <Typography variant="subtitle1" component="h2">
            <b>Premiers pas</b>
          </Typography>
          <IconButton
            size="small"
            aria-label="Réduire"
            onClick={() => setOpen(false)}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </div>
        <Typography variant="body2" color="textSecondary">
          Quelques actions pour bien démarrer.
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(doneCount / items.length) * 100}
          className={classes.progress}
        />
      </div>
      {items.map(item => (
        <ButtonBase
          key={item.id}
          className={classes.item}
          onClick={() => {
            setOpen(false);
            onOpenItem(item);
          }}
        >
          {item.done ? (
            <CheckCircleIcon fontSize="small" className={classes.check} />
          ) : (
            <RadioButtonUncheckedIcon
              fontSize="small"
              className={classes.todo}
            />
          )}
          <span className={classes.itemText}>
            <Typography
              variant="body2"
              className={item.done ? classes.itemDone : undefined}
            >
              <b>{item.title}</b>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {item.description}
            </Typography>
          </span>
        </ButtonBase>
      ))}
      <div className={classes.footer}>
        <Button size="small" onClick={onReplayTour}>
          Revoir la visite
        </Button>
        <Button size="small" onClick={onHide}>
          Masquer
        </Button>
      </div>
    </Paper>
  );
}
